import _defineProperty from "@babel/runtime/helpers/defineProperty";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import moment from 'moment';
import { getServiceTotal, pickValue, sumAmounts } from './billDetailsUtils';
export const round2 = value => Math.round(value * 100) / 100;

/**
 * Empty inputs mean "no value" rather than zero, so totals do not silently drop to 0.
 *
 * Grouping separators are stripped first because `Number('1,240')` is `NaN`: a person typing a
 * comma — which is how the amount is displayed back to them — otherwise loses the figure entirely,
 * and the subtotal above it drops by that much with nothing said.
 */
export function toNumber(value) {
  if (value === '' || value === null || value === undefined) return null;
  const parsed = Number(typeof value === 'string' ? value.replace(/[,\s]/g, '') : value);
  return Number.isFinite(parsed) ? parsed : null;
}

/**
 * Keep the derived amounts in step with whatever the user just typed: a service subtotal follows its
 * line items, a property subtotal follows its services, and the total due follows the properties.
 * Services without line items keep their extracted subtotal, since there is nothing to sum.
 */
export function withRecalculatedTotals(data) {
  if (!data) return data;
  const properties = (data.properties || []).map(property => {
    const services = (property.services || []).map(service => {
      const lineItems = service.lineItems || [];
      return lineItems.length ? _objectSpread(_objectSpread({}, service), {}, {
        subtotal: round2(sumAmounts(lineItems))
      }) : service;
    });
    const subtotal = services.reduce((acc, service) => {
      const total = getServiceTotal(service);
      return acc + (total !== null && total !== void 0 ? total : 0);
    }, 0);
    return _objectSpread(_objectSpread({}, property), {}, {
      services,
      subtotal: round2(subtotal)
    });
  });
  if (!properties.length) return _objectSpread({}, data);

  // Bill-level charges are added on top, exactly as the server does. A late fee is lifted off its
  // utility at extraction (LB-5845), so the properties no longer carry it -- and a total summed from
  // them alone would drop it the moment anybody typed in the editor, showing one number while the
  // server stored another.
  const billCharges = ((data === null || data === void 0 ? void 0 : data.billCharges) || []).reduce((acc, line) => acc + (Number(line === null || line === void 0 ? void 0 : line.amount) || 0), 0);
  return _objectSpread(_objectSpread({}, data), {}, {
    properties,
    totalAmountDue: round2(properties.reduce((acc, p) => acc + p.subtotal, 0) + billCharges)
  });
}
function mapProperties(data, mapper) {
  return _objectSpread(_objectSpread({}, data), {}, {
    properties: ((data === null || data === void 0 ? void 0 : data.properties) || []).map(mapper)
  });
}

/** For edits that move money: the amounts they touch are rolled up before the next render. */
function mapPropertyAmounts(data, mapper) {
  return withRecalculatedTotals(mapProperties(data, mapper));
}

/** Summary fields carry no amounts, so editing them must not touch the extracted totals. */
export function updateBillField(data, patch) {
  return _objectSpread(_objectSpread({}, data), patch);
}

/** Only editable while no line items exist to derive the total from; kept raw until save. */
export function updateTotalDue(data, value) {
  return _objectSpread(_objectSpread({}, data), {}, {
    totalAmountDue: value === '' ? null : value
  });
}
export function removeProperty(data, propertyIndex) {
  return withRecalculatedTotals(_objectSpread(_objectSpread({}, data), {}, {
    properties: ((data === null || data === void 0 ? void 0 : data.properties) || []).filter((property, index) => index !== propertyIndex)
  }));
}
export function updateService(data, propertyIndex, serviceIndex, patch) {
  return mapPropertyAmounts(data, (property, index) => {
    if (index !== propertyIndex) return property;
    const services = (property.services || []).map((service, i) => i === serviceIndex ? _objectSpread(_objectSpread({}, service), patch) : service);
    return _objectSpread(_objectSpread({}, property), {}, {
      services
    });
  });
}

/**
 * The summary states one service period for the bill, so moving it moves every utility on it. A
 * utility that carried its own dates is overwritten, which is what a single row can honestly mean.
 */
export function setServiceDates(data, patch) {
  return mapProperties(_objectSpread(_objectSpread({}, data), patch), property => _objectSpread(_objectSpread({}, property), {}, {
    services: (property.services || []).map(service => _objectSpread(_objectSpread({}, service), patch))
  }));
}

/** Dropping a utility chip drops that utility everywhere, which is what the summary row promises. */
export function removeServiceType(data, serviceType) {
  return mapPropertyAmounts(data, property => _objectSpread(_objectSpread({}, property), {}, {
    services: (property.services || []).filter(service => service.serviceType !== serviceType)
  }));
}
export function setGlCodeForServiceType(data, serviceType, glCode) {
  return mapProperties(data, property => _objectSpread(_objectSpread({}, property), {}, {
    services: (property.services || []).map(service => service.serviceType === serviceType ? _objectSpread(_objectSpread({}, service), {}, {
      glCode
    }) : service)
  }));
}
function mapLineItems(data, propertyIndex, serviceIndex, mapper) {
  return mapPropertyAmounts(data, (property, index) => {
    if (index !== propertyIndex) return property;
    const services = (property.services || []).map((service, i) => i === serviceIndex ? _objectSpread(_objectSpread({}, service), {}, {
      lineItems: mapper(service.lineItems || [])
    }) : service);
    return _objectSpread(_objectSpread({}, property), {}, {
      services
    });
  });
}
export function updateLineItem(data, propertyIndex, serviceIndex, lineIndex, patch) {
  return mapLineItems(data, propertyIndex, serviceIndex, lineItems => lineItems.map((line, index) => index === lineIndex ? _objectSpread(_objectSpread({}, line), patch) : line));
}
export function addLineItem(data, propertyIndex, serviceIndex) {
  return mapLineItems(data, propertyIndex, serviceIndex, lineItems => [...lineItems, {
    amount: null,
    description: ''
  }]);
}
export function removeLineItem(data, propertyIndex, serviceIndex, lineIndex) {
  return mapLineItems(data, propertyIndex, serviceIndex, lineItems => lineItems.filter((line, index) => index !== lineIndex));
}

/**
 * While typing, amount fields hold the raw text so partial input such as "12." survives a keystroke.
 * Saving turns those back into numbers, so nothing downstream has to parse strings. Totals are left
 * as they are: they were already recalculated with every edit.
 */
export function normalizeAmounts(data) {
  if (!data) return data;
  const properties = (data.properties || []).map(property => _objectSpread(_objectSpread({}, property), {}, {
    services: (property.services || []).map(service => _objectSpread(_objectSpread({}, service), {}, {
      lineItems: (service.lineItems || []).map(line => _objectSpread(_objectSpread({}, line), {}, {
        amount: toNumber(line.amount)
      }))
    }))
  }));
  return _objectSpread(_objectSpread({}, data), {}, {
    properties,
    totalAmountDue: toNumber(data.totalAmountDue)
  });
}

/**
 * Stamp every nested node with the position it holds in the payload the editor was handed.
 *
 * Call this exactly once, as the draft opens. The server matches an edited node back to the one it
 * came from by this number and nothing else, because position cannot do the job: remove Water from
 * a bill and Sewer moves up into its place, so a positional walk reports that Water's dates changed
 * when Water is simply gone.
 *
 * Re-running it mid-draft renumbers by *current* position and silently reassigns one row's edits to
 * another — after a removal, the nodes stamped 0 and 2 become 0 and 1, and row 2's changes land on
 * row 1. "Only stamp what is unstamped" is not a safe weakening either: it would give a freshly
 * added line the index of a surviving original. Call-once is the contract.
 *
 * A node with no stamp is a node with no counterpart in the extraction — an insert — which is why
 * `addLineItem` deliberately mints one without.
 */
export function withSourceIndexes(data) {
  if (!data) return data;
  return _objectSpread(_objectSpread({}, data), {}, {
    properties: (data.properties || []).map((property, propertyIndex) => _objectSpread(_objectSpread({}, property), {}, {
      sourceIndex: propertyIndex,
      services: (property.services || []).map((service, serviceIndex) => _objectSpread(_objectSpread({}, service), {}, {
        sourceIndex: serviceIndex,
        lineItems: (service.lineItems || []).map((line, lineIndex) => _objectSpread(_objectSpread({}, line), {}, {
          sourceIndex: lineIndex
        }))
      }))
    }))
  });
}

/**
 * Read a field the editor writes under one key but an older payload may carry under another.
 *
 * Presence, not truthiness. `pickValue` skips an empty string, so asking it for a field somebody has
 * just cleared hands back the stale alias instead and the clear is silently undone — the value
 * reappears on the next render, and now it persists. The aliases are consulted only when the key is
 * absent altogether, which is the one case they exist for.
 */
function ownOrAlias(source, key, aliases) {
  return (source === null || source === void 0 ? void 0 : source[key]) !== undefined ? source[key] : pickValue(source, aliases);
}

/** Trimmed text, or null — the service reads whitespace-only as an erasure either way. */
function text(value) {
  if (value === null || value === undefined) return null;
  const trimmed = String(value).trim();
  return trimmed === '' ? null : trimmed;
}

/**
 * A date as the service will accept it. It takes `yyyy-MM-dd` and nothing else, answering 400 to
 * anything it cannot read, so an extracted date the user never touched is normalised here rather
 * than left to fail the whole save.
 */
function isoDate(value) {
  if (!value) return null;
  const parsed = moment(value);
  return parsed.isValid() ? parsed.format(moment.HTML5_FMT.DATE) : null;
}

/**
 * An amount as typed, canonicalised. The service takes it as text — the editor holds raw input while
 * somebody types, and "12." is a legitimate halfway state — but it parses that text without allowing
 * grouping separators, so "1,240" would reach it and be read as zero.
 */
function amount(value) {
  const parsed = toNumber(value);
  return parsed === null ? null : String(parsed);
}

/**
 * Project a draft down to what the edit endpoint accepts: the fields a person can change, and the
 * source indexes tying each node back to the payload the draft opened on.
 *
 * This is a **whole** payload, never a patch. The service assigns every field on it unconditionally
 * and reads an absent list as "nothing here", so omitting `properties` deletes every property,
 * service and line item, zeroes the bill, and answers 200 with a version bump. Fields the editor
 * offers no control for — `previousBalance`, the bill-level service dates — are carried through for
 * that reason, not by oversight: dropping them erases them.
 *
 * What is deliberately absent is the other half of the bargain: `propertyId`, `utilityId`,
 * `billingCycleId`, the subtotals and the OCR telemetry never leave here, so a client cannot
 * re-target an allocation, erase how the extraction went, or dress a typed value up as a
 * confidently extracted one. The service carries those across itself, by source index.
 */
export function toBillEditPayload(data) {
  return {
    accountNumber: text(data === null || data === void 0 ? void 0 : data.accountNumber),
    billDate: isoDate(ownOrAlias(data, 'billDate', ['postingDate'])),
    customFields: ((data === null || data === void 0 ? void 0 : data.customFields) || []).map(field => ({
      label: text(field === null || field === void 0 ? void 0 : field.label),
      value: text(field === null || field === void 0 ? void 0 : field.value)
    })),
    dueDate: isoDate(ownOrAlias(data, 'dueDate', ['billDueDate'])),
    invoiceNumber: text(ownOrAlias(data, 'invoiceNumber', ['invoiceNo'])),
    previousBalance: toNumber(data === null || data === void 0 ? void 0 : data.previousBalance),
    properties: ((data === null || data === void 0 ? void 0 : data.properties) || []).map(property => {
      var _property$sourceIndex;
      return {
        address: text(property === null || property === void 0 ? void 0 : property.address),
        services: ((property === null || property === void 0 ? void 0 : property.services) || []).map(service => {
          var _service$sourceIndex;
          return {
            lineItems: ((service === null || service === void 0 ? void 0 : service.lineItems) || []).map(line => {
              var _line$sourceIndex;
              return {
                amount: amount(line === null || line === void 0 ? void 0 : line.amount),
                description: text(line === null || line === void 0 ? void 0 : line.description),
                sourceIndex: (_line$sourceIndex = line === null || line === void 0 ? void 0 : line.sourceIndex) !== null && _line$sourceIndex !== void 0 ? _line$sourceIndex : null
              };
            }),
            provider: text(service === null || service === void 0 ? void 0 : service.provider),
            serviceEndDate: isoDate(service === null || service === void 0 ? void 0 : service.serviceEndDate),
            serviceStartDate: isoDate(service === null || service === void 0 ? void 0 : service.serviceStartDate),
            serviceType: text(service === null || service === void 0 ? void 0 : service.serviceType),
            sourceIndex: (_service$sourceIndex = service === null || service === void 0 ? void 0 : service.sourceIndex) !== null && _service$sourceIndex !== void 0 ? _service$sourceIndex : null,
            usage: text(ownOrAlias(service, 'usage', ['totalUsage', 'consumption', 'quantity']))
          };
        }),
        sourceIndex: (_property$sourceIndex = property === null || property === void 0 ? void 0 : property.sourceIndex) !== null && _property$sourceIndex !== void 0 ? _property$sourceIndex : null
      };
    }),
    serviceEndDate: isoDate(data === null || data === void 0 ? void 0 : data.serviceEndDate),
    serviceStartDate: isoDate(data === null || data === void 0 ? void 0 : data.serviceStartDate),
    totalAmountDue: toNumber(data === null || data === void 0 ? void 0 : data.totalAmountDue),
    vendorName: text(data === null || data === void 0 ? void 0 : data.vendorName)
  };
}

/** Allocation inputs are stored per property/utility, since each table is edited on its own. */
export function getAllocationSettingKey(propertyIndex, serviceType) {
  return "".concat(propertyIndex, ":").concat(serviceType || '');
}
export function getAllocationSetting(settings, propertyIndex, serviceType) {
  return (settings === null || settings === void 0 ? void 0 : settings[getAllocationSettingKey(propertyIndex, serviceType)]) || {};
}
export function withAllocationSetting(settings, propertyIndex, serviceType, patch) {
  const key = getAllocationSettingKey(propertyIndex, serviceType);
  return _objectSpread(_objectSpread({}, settings), {}, {
    [key]: _objectSpread(_objectSpread({}, (settings === null || settings === void 0 ? void 0 : settings[key]) || {}), patch)
  });
}